import { useState, useRef, useContext, useEffect } from 'react';
import {
  initialExtraToppingRow,
  RegularMenuCategory as RegularMenuCategoryFallback,
  ComboMenuCategory as ComboMenuCategoryFallback,
  fieldLabelMap,
} from './constants';
import { GlobalStateDataContext } from '../../../context-api/data-provider/GlobalStateDataProvider';
import { ProductDataContext } from '../../../context-api/data-provider/ProductDataProvider';
import { containsUnsafeScriptContent } from '../../../utils/regexValidation';

const isValidDescription = ({ text, name, setInputValidationError }) => {
  setInputValidationError(false);
  if (name === 'recipewith' && !text.trim()) return '';
  if (!text.trim()) return 'This field is required';
  if (name === 'productname' && text.length > 50) {
    setInputValidationError(true);
    return 'Max 50 characters allowed';
  }
  if (name === 'recipewith' && text.length > 25) {
    setInputValidationError(true);
    return 'Max 25 characters allowed';
  }
  if (name === 'quantity' && text.length > 5) {
    setInputValidationError(true);
    return 'Max 5 characters allowed';
  }
  if (name === 'description' && text.length > 500) {
    setInputValidationError(true);
    return 'Max 500 characters allowed';
  }
  if (name === 'quantity' && !/^\d+$/.test(text)) {
    setInputValidationError(true);
    return 'Only numbers allowed';
  }
  if (containsUnsafeScriptContent(text)) {
    setInputValidationError(true);
    return 'Script or unsafe content is not allowed';
  }
  return '';
};

const capitalizeFirst = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : '');

const initOffers = (productPromotion) => {
  const promo = productPromotion || {};
  const hasActive = Object.values(promo).some((v) => v === true);
  return {
    buy_one_get_one_free: promo.buy_one_get_one_free || false,
    buy_two_get_one_free: promo.buy_two_get_one_free || false,
    buy_one_get_half_price: promo.buy_one_get_half_price || false,
    none: !hasActive,
  };
};

const initServingDays = (days) => {
  if (!days || days.length === 0) return [];
  return days.map(capitalizeFirst);
};

const initPrice = (price) => {
  if (price == null || price === '') return '0.00';
  return Number(price).toFixed(2);
};

const formatPrice = (raw = '') => {
  const digitsOnly = String(raw).replace(/\D/g, '').padStart(3, '0');
  const intPart = digitsOnly.slice(0, -2);
  const decimalPart = digitsOnly.slice(-2);
  return parseFloat(`${parseInt(intPart, 10)}.${decimalPart}`);
};

const getProductPromotion = (list) => {
  const validPromotion = Object.entries(list).find(
    ([key, value]) => value === true && key !== 'none'
  );
  return validPromotion
    ? { product_promotion: { [validPromotion[0]]: true } }
    : { product_promotion: {} };
};

// Normalize stored category enums back to display labels
// so we can apply the same max-price rules as in PriceInput.
const normalizeCategoryLabel = (value) => {
  const map = {
    main_dish: 'main dish',
    power_side: 'power side',
    drink_dessert_treat: 'drink or dessert or treat',
    main_course: 'main course',
  };
  return map[value] || value;
};

// Max allowed price by category and product type (matches Price.js / RestaurantProducts)
const maxPriceAllowed = (productCategory, productType) => {
  const normalized = normalizeCategoryLabel(productCategory);

  if (productType === 'non_meal_item') {
    // eslint-disable-next-line default-case
    switch (normalized) {
      case 'appetizers':
        return 9.99;
    }
  }

  if (productType === 'meal_item') {
    // eslint-disable-next-line default-case
    switch (normalized) {
      case 'main dish':
        return 12.49;
      case 'side':
        return 5.5;
      case 'complementry':
        return 0.0;
      case 'power side':
        return 9.5;
      case 'starter':
        return 4.0;
      case 'drink or dessert or treat':
        return 4.0;
    }
  }
  return undefined;
};

export const useEditProductForm = ({ product, onUpdate, onClose }) => {
  const formTopRef = useRef(null);

  const { onLoadInfoResponse } = useContext(GlobalStateDataContext);
  const {
    onUpdateProduct,
    onUploadImageCludinary,
    onGetProductCategory,
    productCategories,
  } = useContext(ProductDataContext);

  const KitchenName = product?.kitchen_name;
  const origRef = useRef({
    product_name: product?.product_name || '',
    recipe_with: product?.recipe_with || '',
    product_description: product?.product_description || '',
    product_category: product?.product_category || 'select',
    special_marking: product?.special_marking?.[0] || 'select',
    serving_temperature: product?.serving_temperature || 'select',
    cuisine: product?.cuisine?.toLowerCase() || 'select',
    size: product?.size || 'full',
    serving_size: product?.serving_size || 'one_person',
    meal_type: product?.meal_type || 'All Day',
    price: initPrice(product?.price),
    available_quantity:
      product?.available_quantity != null ? String(product.available_quantity) : '0',
    serving_days: initServingDays(product?.serving_days),
    ingredients: product?.ingredients?.length ? [...product.ingredients] : [],
    allergic_ingredients: product?.allergic_ingredients?.length
      ? [...product.allergic_ingredients]
      : [],
    extra_toppings: product?.extra_toppings?.length ? [...product.extra_toppings] : [],
    product_promotion: product?.product_promotion || {},
  });

  const [productType] = useState(product?.product_type || 'non_meal_item');
  const [productName, setProductName] = useState(product?.product_name || '');
  const [recipeWith, setRecipeWith] = useState(product?.recipe_with || '');
  const [description, setDescription] = useState(product?.product_description || '');
  const [productCategory, setProductCategory] = useState(product?.product_category || 'select');
  const [specialMarking, setSpecialMarking] = useState(product?.special_marking?.[0] || 'select');
  const [servingTemperature, setServingTemperature] = useState(
    product?.serving_temperature || 'select'
  );
  const [cuisineType, setCuisineType] = useState(product?.cuisine?.toLowerCase() || 'select');
  const [size, setSize] = useState(product?.size || 'full');
  const [servingSize, setServingSize] = useState(product?.serving_size || 'one_person');
  const [mealType, setMealType] = useState(product?.meal_type || 'All Day');
  const [price, setPrice] = useState(initPrice(product?.price));
  const [quantity, setQuantity] = useState(
    product?.available_quantity != null ? String(product.available_quantity) : '0'
  );
  const [selectedDays, setSelectedDays] = useState(initServingDays(product?.serving_days));
  const [offers, setOffers] = useState(initOffers(product?.product_promotion));
  const [ingredients, setIngredients] = useState(
    product?.ingredients?.length ? product.ingredients : ['']
  );
  const [allergicIngredients, setAllergicIngredients] = useState(
    product?.allergic_ingredients?.length ? product.allergic_ingredients : ['']
  );
  const [extraToppings, setExtraToppings] = useState(
    product?.extra_toppings?.length
      ? product.extra_toppings.map((t) => ({ ...t, errors: {} }))
      : [{ ...initialExtraToppingRow }]
  );
  const [imageFile, setImageFile] = useState('');
  const [previewUrl, setPreviewUrl] = useState(product?.image_url || '');
  const [ingredientImageFile, setIngredientImageFile] = useState('');
  const [ingredientPreviewUrl, setIngredientPreviewUrl] = useState(
    product?.ingredients_image || ''
  );

  const productImageCacheRef = useRef({ cacheKey: '', secureUrl: '' });
  const ingredientImageCacheRef = useRef({ cacheKey: '', secureUrl: '' });

  const [errors, setErrors] = useState({
    productname: '',
    recipewith: '',
    description: '',
    quantity: '',
  });
  const [inputValidationError, setInputValidationError] = useState(false);
  const [missingFields, setMissingFields] = useState([]);
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editConfirmed, setEditConfirmed] = useState(false);
  const [editingFields, setEditingFields] = useState({});
  const [changedFields, setChangedFields] = useState({});

  useEffect(() => {
    onGetProductCategory?.();
  }, []);

  const accountType = onLoadInfoResponse?.data?.data?.data?.KitchenInfo?.account_type;
  const apiCategoryData = productCategories?.data || {};
  const RegularMenuCategory = apiCategoryData?.RegularMenuCategory || RegularMenuCategoryFallback;
  const ComboMenuCategory = apiCategoryData?.ComboMenuCategory || ComboMenuCategoryFallback;
  const PromotionalStoreCategory = apiCategoryData?.PromotionalStoreCategory || [];
  const CloudKitchenComboMenuCategory = apiCategoryData?.CloudKitchenComboMenuCategory || [];

  const categoryList = (() => {
    if (accountType === 'cloud kitchen') {
      return CloudKitchenComboMenuCategory.length > 0
        ? CloudKitchenComboMenuCategory
        : productType === 'non_meal_item'
          ? RegularMenuCategory
          : ComboMenuCategory;
    }

    if (accountType === 'promotional kitchen') {
      return PromotionalStoreCategory.length > 0
        ? PromotionalStoreCategory
        : productType === 'non_meal_item'
          ? RegularMenuCategory
          : ComboMenuCategory;
    }

    return productType === 'non_meal_item' ? RegularMenuCategory : ComboMenuCategory;
  })();

  const getFileCacheKey = (file) =>
    file && file.name != null ? `${file.name}-${file.size}-${file.lastModified ?? 0}` : '';

  const startEdit = (key) => setEditingFields((prev) => ({ ...prev, [key]: true }));

  const cancelField = (key, setter) => {
    setter(origRef.current[key]);
    setEditingFields((prev) => {
      const n = { ...prev };
      delete n[key];
      return n;
    });
    setChangedFields((prev) => {
      const n = { ...prev };
      delete n[key];
      return n;
    });
  };

  const trackField = (apiKey, newVal) => {
    const origVal = origRef.current[apiKey];
    setChangedFields((prev) => {
      const next = { ...prev };
      if (String(newVal ?? '') !== String(origVal ?? '')) {
        next[apiKey] = newVal;
      } else {
        delete next[apiKey];
      }
      return next;
    });
  };

  const onChangeHandler = (key) => (e) => {
    setInputValidationError(false);
    const val = e.target.value;
    const newErrors = { ...errors };
    if (key === 'productname') {
      setProductName(val);
      trackField('product_name', val);
      newErrors.productname = isValidDescription({
        text: val,
        name: 'productname',
        setInputValidationError,
      });
    }
    if (key === 'recipewith') {
      setRecipeWith(val);
      trackField('recipe_with', val);
      newErrors.recipewith = isValidDescription({
        text: val,
        name: 'recipewith',
        setInputValidationError,
      });
    }
    if (key === 'description') {
      setDescription(val);
      trackField('product_description', val);
      newErrors.description = isValidDescription({
        text: val,
        name: 'description',
        setInputValidationError,
      });
    }
    if (key === 'quantity') {
      setQuantity(val);
      trackField('available_quantity', val);
      newErrors.quantity = isValidDescription({
        text: val,
        name: 'quantity',
        setInputValidationError,
      });
    }
    setErrors(newErrors);
  };

  const cleanedIngredients = [...new Set(ingredients.filter((item) => item.trim() !== ''))];
  const cleanedAllergicIngredients = [
    ...new Set(allergicIngredients.filter((item) => item.trim() !== '')),
  ];
  const cleanedToppings = extraToppings.filter(
    (t) => t.topping_name.trim() !== '' && Number(t.topping_price) !== 0
  );
  const cleanExtraToppings = cleanedToppings.map(
    ({ errors: _e, topping_price, topping_available_quantity, ...rest }) => ({
      ...rest,
      topping_price: formatPrice(topping_price),
      topping_available_quantity: parseInt(topping_available_quantity, 10),
    })
  );

  const resultPromotion = getProductPromotion(offers);

  const hasServingDaysChanged = () => {
    const orig = [...origRef.current.serving_days].sort();
    const curr = [...selectedDays].sort();
    return JSON.stringify(orig) !== JSON.stringify(curr);
  };

  const hasToppingsChanged = () => {
    const origRaw = origRef.current.extra_toppings || [];
    const origClean = origRaw.map(
      ({ errors: _e, topping_price, topping_available_quantity, ...rest }) => ({
        ...rest,
        topping_price: parseFloat(topping_price) || 0,
        topping_available_quantity: parseInt(topping_available_quantity, 10) || 0,
      })
    );
    return JSON.stringify(origClean) !== JSON.stringify(cleanExtraToppings);
  };

  const hasPromotionChanged = () => {
    const origPromo = origRef.current.product_promotion || {};
    const currPromo = resultPromotion.product_promotion || {};
    const activeOrig = Object.entries(origPromo).find(([, v]) => v === true)?.[0] ?? null;
    const activeCurr = Object.entries(currPromo).find(([, v]) => v === true)?.[0] ?? null;
    return activeOrig !== activeCurr;
  };

  const hasIngredientsChanged = () => {
    const origSorted = [
      ...new Set((origRef.current.ingredients || []).filter((i) => i.trim() !== '')),
    ].sort();
    return JSON.stringify(origSorted) !== JSON.stringify([...cleanedIngredients].sort());
  };

  const hasAllergicChanged = () => {
    const origSorted = [
      ...new Set((origRef.current.allergic_ingredients || []).filter((i) => i.trim() !== '')),
    ].sort();
    return JSON.stringify(origSorted) !== JSON.stringify([...cleanedAllergicIngredients].sort());
  };

  const handleUpdateProduct = async () => {
    setMissingFields([]);
    const errorMsg = [];

    if (inputValidationError) {
      errorMsg.push({
        errorMessage: 'Please check your input — a required field may be missing or invalid.',
        param: 'input_box',
      });
    }
    if (!productName.trim()) {
      errorMsg.push({ errorMessage: 'Product name is required', param: 'product_name' });
    }
    if (!price || price === '0.00') {
      errorMsg.push({ errorMessage: 'Price is required', param: 'price' });
    }

    const maxAllowed = maxPriceAllowed(productCategory, productType);
    if (maxAllowed != null) {
      const numericPrice = Number(price);
      if (!Number.isNaN(numericPrice) && numericPrice > maxAllowed) {
        errorMsg.push({
          errorMessage: `Price cannot exceed $${maxAllowed.toFixed(
            2
          )} for this product category and type.`,
          param: 'price',
        });
      }
    }
    if (selectedDays.length === 0) {
      errorMsg.push({ errorMessage: 'Serving days required', param: 'serving_days' });
    }
    if (servingTemperature === 'select') {
      errorMsg.push({ errorMessage: 'Serving temperature required', param: 'serving_temperature' });
    }
    if (specialMarking === 'select') {
      errorMsg.push({ errorMessage: 'Special marking required', param: 'special_marking' });
    }
    if (productCategory === 'select') {
      errorMsg.push({ errorMessage: 'Product category is required', param: 'product_category' });
    }
    if (!cuisineType || cuisineType === 'select') {
      errorMsg.push({ errorMessage: 'Cuisine is required', param: 'cuisine' });
    }

    // Mirror create-form validation for description (recipe_with is optional on edit)
    if (!description || description.trim() === '') {
      errorMsg.push({
        errorMessage: 'Product description is required',
        param: 'product_description',
      });
    }

    // if (recipeWith == null || String(recipeWith ?? '').trim() === '') {
    //   errorMsg.push({ errorMessage: 'Recipe with is required', param: 'recipe_with' });
    // }

    // Mirror extra toppings validation from create-form for non_meal_item
    if (productType === 'non_meal_item' && extraToppings?.length) {
      extraToppings.forEach((t, index) => {
        const hasName = t.topping_name?.trim();
        if (!hasName) return;

        const missing = [];
        const priceVal = t.topping_price;
        const qtyVal = t.topping_available_quantity;

        const hasPrice =
          priceVal !== undefined &&
          priceVal !== null &&
          String(priceVal).trim() !== '' &&
          Number(parseFloat(String(priceVal).replace(/\D/g, ''))) > 0;

        const hasQty =
          qtyVal !== undefined &&
          qtyVal !== null &&
          String(qtyVal).trim() !== '' &&
          !Number.isNaN(Number(qtyVal)) &&
          Number(qtyVal) >= 0;

        if (!hasPrice) missing.push('price');
        if (!hasQty) missing.push('quantity');

        if (missing.length) {
          errorMsg.push({
            errorMessage: `Extra topping "${hasName}" (row ${index + 1}): please add ${missing.join(' and ')}.`,
            param: 'extra_topping',
          });
        }
      });
    }
    console.log('----errorMsg', errorMsg);
    if (errorMsg.length > 0) {
      setMissingFields(errorMsg);
      setTimeout(() => {
        if (formTopRef.current) {
          formTopRef.current.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }
      }, 100);
      return;
    }

    const updatePayload = {
      kitchen_name: KitchenName,
      product_sku: product?.product_sku,
      product_category: productCategory,
    };

    if ('product_name' in changedFields) updatePayload.product_name = productName;
    if ('recipe_with' in changedFields) updatePayload.recipe_with = recipeWith;
    if ('product_description' in changedFields) updatePayload.product_description = description;
    if ('product_category' in changedFields) updatePayload.product_category = productCategory;
    if ('special_marking' in changedFields) updatePayload.special_marking = specialMarking;
    if ('serving_temperature' in changedFields)
      updatePayload.serving_temperature = servingTemperature;
    if ('cuisine' in changedFields) updatePayload.cuisine = cuisineType;
    if ('size' in changedFields) updatePayload.size = size;
    if ('serving_size' in changedFields) updatePayload.serving_size = servingSize;
    if ('meal_type' in changedFields) updatePayload.meal_type = mealType;
    if ('price' in changedFields) updatePayload.price = formatPrice(price);
    if ('available_quantity' in changedFields)
      updatePayload.available_quantity = parseInt(quantity, 10);
    if (hasServingDaysChanged())
      updatePayload.serving_days = selectedDays.map((d) => d.toLowerCase());
    if (productType === 'non_meal_item' && hasToppingsChanged())
      updatePayload.extra_toppings = cleanExtraToppings;
    if (productType === 'non_meal_item' && hasPromotionChanged())
      updatePayload.product_promotion = resultPromotion.product_promotion;
    if (hasIngredientsChanged()) updatePayload.ingredients = cleanedIngredients;
    if (hasAllergicChanged()) updatePayload.allergic_ingredients = cleanedAllergicIngredients;

    if (imageFile && KitchenName) {
      const productKey = getFileCacheKey(imageFile);
      const cached = productImageCacheRef.current;
      if (cached.cacheKey === productKey && cached.secureUrl) {
        updatePayload.image_url = cached.secureUrl;
      } else {
        const formData = new FormData();
        formData.append('file', imageFile);
        formData.append('fileName', `${KitchenName}_${product?.product_sku}`);
        formData.append('kitchen_name', KitchenName);
        const response = await onUploadImageCludinary(formData);

        if (response?.data?.success) {
          const secureUrl = response?.data?.secureUrl;
          productImageCacheRef.current = { cacheKey: productKey, secureUrl };
          updatePayload.image_url = secureUrl;
        } else {
          setMissingFields([
            {
              errorMessage:
                'Something went wrong during product image upload. Please try again later or contact support.',
              param: 'image_url',
            },
          ]);
          setTimeout(() => {
            if (formTopRef.current) {
              formTopRef.current.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }, 100);
          return;
        }
      }
    }

    if (ingredientImageFile && KitchenName) {
      const ingredientKey = getFileCacheKey(ingredientImageFile);
      const cachedIng = ingredientImageCacheRef.current;
      if (cachedIng.cacheKey === ingredientKey && cachedIng.secureUrl) {
        updatePayload.ingredients_image = cachedIng.secureUrl;
      } else {
        const formData = new FormData();
        formData.append('file', ingredientImageFile);
        formData.append('fileName', `${KitchenName}_${'ingredientImage'}_${product?.product_sku}`);
        formData.append('kitchen_name', KitchenName);
        const responseTwo = await onUploadImageCludinary(formData);

        if (responseTwo?.data?.success) {
          const ingredientSecureUrl = responseTwo?.data?.secureUrl;
          ingredientImageCacheRef.current = {
            cacheKey: ingredientKey,
            secureUrl: ingredientSecureUrl,
          };
          updatePayload.ingredients_image = ingredientSecureUrl;
        } else {
          setMissingFields([
            {
              errorMessage:
                'Something went wrong during ingredients image upload. Please try again later or contact support.',
              param: 'ingredients_image',
            },
          ]);
          setTimeout(() => {
            if (formTopRef.current) {
              formTopRef.current.scrollTo({ top: 0, behavior: 'smooth' });
            }
          }, 100);
          return;
        }
      }
    }

    console.log('---- Partial Update Payload', updatePayload);

    setIsLoading(true);
    try {
      const response = await onUpdateProduct(updatePayload);

      if (response?.success) {
        if (typeof onUpdate === 'function') {
          onUpdate(response?.product || updatePayload);
        }
        if (typeof onClose === 'function') {
          onClose();
        }
        setOpenSnackBar(true);
      } else if (Array.isArray(response?.errors) && response.errors.length > 0) {
        // Map backend validation errors into the same shape as create-form errors
        setMissingFields(response.errors);
        setTimeout(() => {
          if (formTopRef.current) {
            formTopRef.current.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }, 100);
      } else {
        // Generic failure
        setMissingFields([
          {
            errorMessage:
              response?.CustomMessage || 'Something went wrong while updating the product.',
            param: 'update_product',
          },
        ]);
        setTimeout(() => {
          if (formTopRef.current) {
            formTopRef.current.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }, 100);
      }
    } catch (err) {
      console.error('Update product error', err);
      setMissingFields([
        {
          errorMessage: 'Unexpected error while updating product. Please try again.',
          param: 'update_product',
        },
      ]);
      setTimeout(() => {
        if (formTopRef.current) {
          formTopRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    } finally {
      setIsLoading(false);
    }
  };

  const changedCount = Object.keys(changedFields).length;

  const pendingFields = [];
  Object.keys(changedFields).forEach((key) => {
    if (fieldLabelMap[key]) pendingFields.push(fieldLabelMap[key]);
  });
  if (hasServingDaysChanged()) pendingFields.push(fieldLabelMap.serving_days);
  if (productType === 'non_meal_item' && hasToppingsChanged())
    pendingFields.push(fieldLabelMap.extra_toppings);
  if (productType === 'non_meal_item' && hasPromotionChanged())
    pendingFields.push(fieldLabelMap.product_promotion);
  if (hasIngredientsChanged()) pendingFields.push(fieldLabelMap.ingredients);
  if (hasAllergicChanged()) pendingFields.push(fieldLabelMap.allergic_ingredients);
  if (imageFile) pendingFields.push(fieldLabelMap.image_url);
  if (ingredientImageFile) pendingFields.push(fieldLabelMap.ingredients_image);
  const fieldsToBeUpdatedUnique = [...new Set(pendingFields)];

  return {
    formTopRef,
    productType,
    productName,
    setProductName,
    recipeWith,
    setRecipeWith,
    description,
    setDescription,
    productCategory,
    setProductCategory,
    specialMarking,
    setSpecialMarking,
    servingTemperature,
    setServingTemperature,
    cuisineType,
    setCuisineType,
    size,
    setSize,
    servingSize,
    setServingSize,
    mealType,
    setMealType,
    price,
    setPrice,
    quantity,
    setQuantity,
    selectedDays,
    setSelectedDays,
    offers,
    setOffers,
    ingredients,
    setIngredients,
    allergicIngredients,
    setAllergicIngredients,
    extraToppings,
    setExtraToppings,
    imageFile,
    setImageFile,
    previewUrl,
    setPreviewUrl,
    ingredientImageFile,
    setIngredientImageFile,
    ingredientPreviewUrl,
    setIngredientPreviewUrl,
    errors,
    missingFields,
    openSnackBar,
    setOpenSnackBar,
    isLoading,
    editConfirmed,
    setEditConfirmed,
    editingFields,
    changedFields,
    changedCount,
    categoryList,
    fieldsToBeUpdatedUnique,
    startEdit,
    cancelField,
    trackField,
    onChangeHandler,
    handleUpdateProduct,
  };
};
